import { ArrowRight, Plus, RefreshCw, Trash2 } from "lucide-react";
import Select from "react-select";
import type { TransformEntry } from "../facet-form-config";
import { INPUT_CLASS } from "./FacetFormField";
import {
    getFacetSelectStyles,
    type FacetSelectOption,
    toFacetSelectOptions,
    FACET_SELECT_EMPTY_VALUE_LABEL,
} from "./facet-select-styles";

interface TransformEditorProps {
    entries: TransformEntry[];
    options: string[];
    onChange: (entries: TransformEntry[]) => void;
    onApplyTransform?: () => void;
    isApplying?: boolean;
    canApplyTransform?: boolean;
    optionsRefreshKey?: number;
}

export default function TransformEditor({
    entries,
    options,
    onChange,
    onApplyTransform,
    isApplying = false,
    canApplyTransform = true,
    optionsRefreshKey = 0,
}: TransformEditorProps) {
    const selectOptions = toFacetSelectOptions(options);

    const update = (index: number, field: "key" | "value", val: string) => {
        const next = entries.map((e, i) => (i === index ? { ...e, [field]: val } : e));
        onChange(next);
    };

    const addRow = () => onChange([...entries, { key: "", value: "" }]);

    const removeRow = (index: number) => {
        const next = entries.filter((_, i) => i !== index);
        onChange(next.length > 0 ? next : [{ key: "", value: "" }]);
    };

    return (
        <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <div className="hidden grid-cols-[1fr_16px_1fr_32px] items-center gap-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 sm:grid">
                <span>Original</span>
                <span />
                <span>Mapped to</span>
                <span />
            </div>
            {entries.map((entry, i) => {
                const isPlaceholderRow = entry.key === "" && entry.value.trim() === "";
                const originalLabel = entry.key === "" ? FACET_SELECT_EMPTY_VALUE_LABEL : entry.key;
                const orphanOption: FacetSelectOption | null =
                    isPlaceholderRow || options.includes(entry.key)
                        ? null
                        : { value: entry.key, label: originalLabel };
                const mergedOptions = [...(orphanOption ? [orphanOption] : []), ...selectOptions];
                const canSelectEmpty = selectOptions.some((o) => o.value === "");
                const selectValue =
                    isPlaceholderRow || (entry.key === "" && !canSelectEmpty)
                        ? null
                        : { value: entry.key, label: originalLabel };

                return (
                    <div
                        key={i}
                        className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[1fr_16px_1fr_32px]"
                    >
                        <Select<FacetSelectOption, false>
                            key={`transform-${optionsRefreshKey}-${i}-${options.join("|")}`}
                            isClearable={false}
                            options={mergedOptions}
                            value={selectValue}
                            placeholder="Select value"
                            classNamePrefix="transform-select"
                            styles={getFacetSelectStyles(false)}
                            onChange={(selectedOption) => update(i, "key", selectedOption?.value ?? "")}
                        />
                        <ArrowRight size={14} className="hidden shrink-0 text-slate-300 sm:block" />
                        <input
                            type="text"
                            value={entry.value}
                            onChange={(e) => update(i, "value", e.target.value)}
                            placeholder="Mapped value"
                            className={INPUT_CLASS}
                        />
                        <button
                            type="button"
                            onClick={() => removeRow(i)}
                            className="shrink-0 justify-self-center rounded-md p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                );
            })}
            <div className="mt-1 flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={addRow}
                    className="flex w-fit items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-semibold text-indigo-600 transition hover:bg-indigo-50"
                >
                    <Plus size={13} /> Add mapping
                </button>
                {onApplyTransform && (
                    <button
                        type="button"
                        onClick={onApplyTransform}
                        disabled={isApplying || !canApplyTransform}
                        className="flex w-fit items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <RefreshCw size={13} className={isApplying ? "animate-spin" : ""} />
                        {isApplying ? "Applying…" : "Apply transform"}
                    </button>
                )}
            </div>
        </div>
    );
}
